import { Button, Modal } from 'antd';

import { useBulkEditSave } from 'app/hooks/data/useBulkEditSave';
import type { IBulkEditPayload } from 'app/typings/bulkEdit';
import { groupBy } from 'app/utils/array';
import { formatDateWithoutHour } from 'app/utils/dates';
import type { Interval } from 'app/utils/dayjs';
import { pluralize } from 'app/utils/strings';
import { amplitudeTrack } from 'app/utils/track/amplitude';

import { PricingRuleBulkEditAlert } from '../PricingRule/_components/PricingRuleBulkEditAlert';
import type { Opening } from '../type';

import './ValidationBulkEditModal.scss';

type Props = {
  handleClose: () => void;
  setIsValidationBulkEditModalOpen: (
    isValidationBulkEditModalOpen: boolean
  ) => void;
  hotelId: number;
  currentRange: Interval;
  payload: IBulkEditPayload;
  dryRunResults: Opening[];
  autoDiscountRate?: number;
  nbPackages: number;
};

export const ValidationBulkEditModal = ({
  handleClose,
  setIsValidationBulkEditModalOpen,
  hotelId,
  currentRange,
  payload,
  dryRunResults,
  autoDiscountRate,
  nbPackages,
}: Props) => {
  const nbPublishedDays = groupBy(
    dryRunResults.filter(({ published }) => published),
    (opening) => opening.date
  ).length;

  const nbImpactedPackages =
    payload.bar !== undefined || payload.stock !== undefined
      ? nbPackages
      : payload.packagesRates.length;

  const nbErrors = dryRunResults.filter(({ published }) => !published).length;

  const bulkEditSave = useBulkEditSave(
    hotelId,
    formatDateWithoutHour(currentRange.start as Date)
  );

  const handleCancel = () => {
    setIsValidationBulkEditModalOpen(false);
    amplitudeTrack('Return to Bulk Edit Changes', {
      errors: nbErrors,
    });
  };

  const handleSaveChange = () => {
    bulkEditSave?.mutate({ ...payload, dryRun: false });

    amplitudeTrack('Accept Bulk Edit Changes', {
      stockPublished: nbPublishedDays,
      stockNotPublished: nbErrors,
      nbAutoDiscountDeactivated: autoDiscountRate
        ? nbPublishedDays + nbErrors
        : 0,
    });

    setIsValidationBulkEditModalOpen(false);
    handleClose();
  };

  return (
    <Modal
      className="modal"
      open={true}
      title="Apply your changes?"
      onCancel={handleCancel}
      wrapClassName="validationBulkEditModalClassname"
      footer={[
        <Button size="large" key="back" type="ghost" onClick={handleCancel}>
          Back to edit
        </Button>,
        <Button
          key="save"
          size="large"
          type="primary"
          htmlType="submit"
          onClick={handleSaveChange}
        >
          Save changes
        </Button>,
      ]}
    >
      <p>By saving your changes, you will apply the following modifications:</p>
      <ul>
        {nbPublishedDays > 0 && (
          <li>
            <span className="modal__validDays">
              {`${pluralize(nbPublishedDays, 'day', 'days')} across ${pluralize(
                nbImpactedPackages,
                'package',
                'packages'
              )}`}
            </span>{' '}
            will be edited and published
          </li>
        )}

        {nbErrors > 0 && (
          <li>
            <span className="modal__errorDays">{nbErrors} packages days</span>{' '}
            have errors and will be edited but not published
          </li>
        )}
      </ul>
      {autoDiscountRate && <PricingRuleBulkEditAlert />}
    </Modal>
  );
};
